import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ApiService } from '../../lib/api.service';
import { StorageService } from '../../lib/storage.service';
import { FileService } from '../../lib/file.service';
import { LogService } from '../../lib/log.service';
import { Router } from '@angular/router';
import { User } from '../../models/user';
import { Organization } from '../../models/organization';
import { ModalService } from '../../lib/modal.service';
import { Invite } from '../../components/invite/invite';
import { TranslateService } from '@ngx-translate/core';

import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { animate, state, style, transition, trigger } from '@angular/animations';

import * as _ from 'lodash';
import moment from 'moment';
import { AnalyticEvent } from 'app/lib/analytic-event';

@Component({
    selector: 'organization-listing.component',
    templateUrl: 'organization-listing.component.html',
    styleUrls: ['./organization-listing.scss'],
    animations: [
        trigger('detailExpand', [
            state('void', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('*', style({ height: '*', visibility: 'visible' })),
            transition('void <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class OrganizationListingPage implements OnInit, OnDestroy {
	users: User[] = [];
	popup: any;
	organizations: Organization[];
	activeorganizations: Organization[];
	disabledOrganizations: Organization[];
	demoOrganizations: Organization[];
	isLoaded: number = 0;
	settings: any;
	cols: any = [];
	disCols: any = [];
	demoCols: any = [];
	organizationsAuth: Array<object>;
	organizationResources: Array<object>;
	isExporting: boolean = false;

	constructor(
		private api: ApiService,
		private log: LogService,
		private modalService: ModalService,
		private fileService: FileService,
		private translate: TranslateService,
		private router: Router
	) {
		this.cols.push('');
		this.cols.push('');
		this.cols.push('');
		this.cols.push('');
		this.disCols.push('');
		this.disCols.push('');
		this.disCols.push('');
		this.disCols.push('');
		this.demoCols.push('');
		this.demoCols.push('');
		this.demoCols.push('');
		this.demoCols.push('');
	}

	ngOnInit() {

		this.translate.stream('error').subscribe((res: any) => {
			this.popup = res;
		});

		this.loadMore();
		this.loadResourceOrgs();
		this.loadAuth();

	}
	inviteProfessional() {
		this.modalService.showComponent(Invite);
	}

	/**
	 * Column index -> organization property. The `assigned_user_name` column
	 * lives on the nested crmGroup, so it is read separately.
	 */
	private static readonly FILTER_KEYS = ['name', 'subdomain', 'allowedDomains', 'assigned_user_name'];

	/**
	 * Applies every non-empty column filter at once. Each column used to be
	 * evaluated on its own against the unfiltered list, so filling in a second
	 * column silently discarded the first one.
	 */
	private applyFilters(rows: Organization[], cols: string[]): Organization[] {
		return rows.filter(org =>
			cols.every((rawValue, index) => {
				const filterValue = (rawValue || '').trim().toLowerCase();
				if (!filterValue) {
					return true;
				}

				const key = OrganizationListingPage.FILTER_KEYS[index];
				if (!key) {
					return true;
				}

				const value = key === 'assigned_user_name'
					? org.crmGroup?.assigned_user_name
					: org[key];

				return !!value && String(value).toLowerCase().indexOf(filterValue) !== -1;
			})
		);
	}

	valueChange(i) {
		this.activeorganizations = this.applyFilters(
			this.organizations.filter(org => org.active),
			this.cols
		);
	}

	disabledValueChange(i) {
		this.disabledOrganizations = this.applyFilters(
			this.organizations.filter(org => !org.active),
			this.disCols
		);
	}

	demoValueChange(i) {
		this.demoOrganizations = this.applyFilters(
			this.organizations.filter(org => org.demoStatus),
			this.demoCols
		);
	}

	doCreate() {
		this.router.navigate(['/admin/organization/new']);
	}

	onEdit(id) {
		this.router.navigate(['/admin/organization/' + id + '/edit']);
	}

	loadMore() {
		this.api.get('admin/organizations').subscribe(
			(results: any) => {
				this.organizations = Organization.initializeArray(results.data);
				this.activeorganizations = this.organizations.filter(org => org.active);
				this.disabledOrganizations = this.organizations.filter(org => !org.active);
				this.demoOrganizations = this.organizations.filter(org => org.demoStatus);
				this.log.event(AnalyticEvent.event.organizationLoaded)
				this.isLoaded++;
				this.restoreScrollPosition();
			},
			(error: any) => {
				this.log.error('Error loading. ' + error.message);
				this.isLoaded++;

			}
		);
	}

	loadAuth() {
		this.api.get('admin/organizationsauth').subscribe(
			(results: any) => {
				this.organizationsAuth = results.data;
				this.isLoaded++;
				this.restoreScrollPosition();
			},
			(error: any) => {


			}
		);
	}

	loadResourceOrgs() {
		this.api.get('admin/organizationsressources').subscribe(
			(results: any) => {
				this.isLoaded++;
				this.organizationResources = results.data;
				this.restoreScrollPosition();
			},
			(error: any) => {


			}
		);
	}

	onActivate(id: number, activate = true){
		const activateString = activate? "Activate": "Deactivate";

		this.modalService.showConfirmation(activateString, `Are you sure you want to ${activateString.toLowerCase()} the organization?`).afterClosed().subscribe((result) => {
			if(result){
				this.api.post('organizations/activate',  {
					OrgID: id,
					Active: activate? 1: 0
				  }).subscribe(
					(results: any) => {
						const index = _.findIndex(this.organizations, { id });
						this.organizations[index].active = activate;
						// Re-run the column filters so the admin does not lose
						// what they had narrowed the list down to.
						this.valueChange(0);
						this.disabledValueChange(0);
						this.demoValueChange(0);
					},
					(error: any) => {
						this.log.error('Error loading. ' + error.message);

					}
				);
			}
		});
	}

	onEditAuth(id) {
		this.router.navigate(['/admin/organization-auth/' + id + '/edit']);
	}

	/**
	 * admin/organizations returns the slim payload that feeds this table — no
	 * address, phone, website, emergency contact or resources. Exporting
	 * straight from it printed "none" in every one of those columns for every
	 * organization. admin/organizationsexport carries the real values.
	 */
	private loadExportData(onLoaded: (organizations: any[]) => void) {
		if (this.isExporting) {
			return;
		}
		this.isExporting = true;

		this.api.get('admin/organizationsexport').subscribe(
			(results: any) => {
				this.isExporting = false;
				onLoaded((results.data || []).filter((organization: any) => organization.Active));
			},
			(error: any) => {
				this.isExporting = false;
				this.log.error('Error loading. ' + error.message);
				this.modalService.showAlert('Error', 'Could not build the export. Please try again.');
			}
		);
	}

	exportListingCsv() {
		this.loadExportData(organizations => {
			const data = organizations.map(organization => ({
				'name': organization.Name,
				'subdomain': organization.Subdomain ? organization.Subdomain : 'none',
				'logo': organization.Logo ? organization.Logo : 'none',
				'address': organization.Address ? organization.Address : 'none',
				'phone': organization.Phone ? organization.Phone : 'none',
				'website': organization.Website ? organization.Website : 'none',
				'emergencyContact': organization.EmergencyContact ? organization.EmergencyContact : 'none',
				'description': organization.Description ? organization.Description : 'none',
				'allowedDomains': organization.allowedDomains ? organization.allowedDomains : 'none',
				'enableResources': organization.enableResources ? true : false,
				'resources': organization.Resources ? organization.Resources : 'none',
			}));

			let options = {
				fieldSeparator: ',',
				quoteStrings: '"',
				decimalseparator: '.',
				showLabels: true,
				showTitle: false,
				useBom: true,
				noDownload: false,
				headers: [
					'Name',
					'Subdomain',
					'Logo',
					'Address',
					'Phone',
					'Website',
					'Emergency Contact',
					'Description',
					'Allowed Domains',
					'Enable Resources',
					'Resources']
			};

			new AngularCsv(data, 'Organization listing -' + moment().format('MMMM Do YYYY, h:mm:ss a'), options);
		});
	}

	exportResourcesCsv() {
		this.loadExportData(organizations => {
			var data = [];

			organizations.forEach(organization => {
				if (!organization.enableResources) {
					return;
				}

				data.push({
					'name': organization.Name,
					'resources': organization.Resources ? organization.Resources : 'none',
					'title': '',
					'summary': '',
					'groupTitle': '',
					'groupAddress': '',
					'groupContact': '',
					'groupAlternateContact': '',
					'groupWebsite': '',
				});

				(organization.resourceSet || []).forEach(resourceSet => {
					data.push({
						'name': '',
						'resources': '',
						'title': resourceSet.title,
						'summary': resourceSet.summary,
						'groupTitle': '',
						'groupAddress': '',
						'groupContact': '',
						'groupAlternateContact': '',
						'groupWebsite': '',
					});

					(resourceSet.resourcesetGroup || []).forEach(group => {
						data.push({
							'name': '',
							'resources': '',
							'title': '',
							'summary': '',
							'groupTitle': group.title,
							'groupAddress': group.address,
							'groupContact': group.contact,
							'groupAlternateContact': group.alternateContact,
							'groupWebsite': group.website,
						});
					});
				});
			});

			let options = {
				fieldSeparator: ',',
				quoteStrings: '"',
				decimalseparator: '.',
				showLabels: true,
				showTitle: false,
				useBom: true,
				noDownload: false,
				// One header per column: the group rows carry five more fields
				// than the four headers this export used to declare.
				headers: [
					'Organization Name',
					'General Resource Description',
					'Resource Title',
					'Resource Summary',
					'Group Title',
					'Group Address',
					'Group Contact',
					'Group Alternate Contact',
					'Group Website',
				]
			};
			new AngularCsv(data, 'Organization resources -' + moment().format('MMMM Do YYYY, h:mm:ss a'), options);
		});
	}

	doClone() {
		this.router.navigate(['/admin/organization/clone']);
	}

	ngOnDestroy() {
		sessionStorage.setItem('orgListingScrollY', String(window.scrollY));
	}

	private restoreScrollPosition() {
		if (this.isLoaded < 3) {
			return;
		}
		const savedY = sessionStorage.getItem('orgListingScrollY');
		if (savedY) {
			sessionStorage.removeItem('orgListingScrollY');
			// Wait for Angular change detection to render DOM, then wait for browser paint
			setTimeout(() => {
				requestAnimationFrame(() => {
					window.scrollTo(0, parseInt(savedY, 10));
				});
			}, 0);
		}
	}
}
